/**
 * Systems Equipment Data Constants
 * Navigation, Communication, and Electrical equipment options for Step 6
 */

// Navigation Equipment options
export const navigationEquipmentOptions = [
  { 
    value: 'gps', 
    label: 'GPS Navigation System', 
    help: 'Electronic position fixing system for navigation (dual GPS required for Cat 0-2)',
    reference: 'WBC3 Section 19.7.1.1',
    category: 'mandatory',
    mandatoryForCategories: [0, 1, 2]
  },
  { 
    value: 'echo_sounder', 
    label: 'Echo Sounder', 
    help: 'Depth sounder for safe navigation - required for all commercial vessels',
    reference: 'WBC3 Section 19.5.1',
    category: 'mandatory',
    mandatoryForCategories: [0, 1, 2, 3, 4, 5, 6]
  },
  { 
    value: 'ais', 
    label: 'AIS (Automatic Identification System)', 
    help: 'Automatic vessel identification and tracking system',
    reference: 'WBC3 Section 19.7.1.4',
    category: 'mandatory',
    mandatoryForCategories: [0, 1, 2]
  },
  { 
    value: 'radar', 
    label: '3cm Radar System', 
    help: '3cm radar on appropriate standard (MIN 698) for collision avoidance and navigation',
    reference: 'WBC3 Section 19.7.1.3',
    category: 'mandatory',
    mandatoryForCategories: [0, 1, 2]
  },
  { 
    value: 'navtex', 
    label: 'NAVTEX Receiver', 
    help: 'Automatic reception of navigational and meteorological warnings',
    reference: 'WBC3 Section 17.5'
  },
  { 
    value: 'gps_backup', 
    label: 'Additional GPS Unit', 
    help: 'Secondary GPS system for redundancy',
    reference: 'WBC3 Section 19.2'
  },
  { 
    value: 'ecdis', 
    label: 'ECDIS/Electronic Charts', 
    help: 'Electronic Chart Display and Information System',
    reference: 'SOLAS V/19'
  },
  { 
    value: 'chart_plotter', 
    label: 'Chart Plotter', 
    help: 'GPS-integrated navigation plotter with electronic charts',
    reference: 'WBC3 Section 19.4'
  },
  { 
    value: 'autopilot', 
    label: 'Autopilot System', 
    help: 'Automatic steering system for course keeping',
    reference: 'WBC3 Section 19.6'
  },
  { 
    value: 'gyro_compass', 
    label: 'Gyro Compass', 
    help: 'Gyroscopic compass for precise heading reference',
    reference: 'WBC3 Section 19.7'
  },
  { 
    value: 'speed_log', 
    label: 'Speed/Distance Log', 
    help: 'Electronic log for speed and distance measurement',
    reference: 'WBC3 Section 19.8'
  },
  { 
    value: 'searchlight', 
    label: 'Navigation Searchlight', 
    help: 'High-intensity searchlight for night navigation and search',
    reference: 'WBC3 Section 14.12',
    category: 'conditional',
    mandatoryForVesselTypes: ['pilot']
  }
];

// Communication Equipment options
export const communicationEquipmentOptions = [
  { 
    value: 'vhf_radio_dsc', 
    label: 'VHF Radio with DSC', 
    help: 'VHF marine radio with Digital Selective Calling capability',
    reference: 'WBC3 Section 17.1'
  },
  { 
    value: 'epirb', 
    label: 'EPIRB (406MHz with GPS)', 
    help: 'Emergency Position Indicating Radio Beacon for distress alerts',
    reference: 'WBC3 Table 17.2.1'
  },
  { 
    value: 'mf_hf_radio', 
    label: 'MF/HF Radio with DSC', 
    help: 'Medium/High Frequency radio for long-range communication with DSC',
    reference: 'WBC3 Table 17.2.1'
  },
  { 
    value: 'satellite_service', 
    label: 'Satellite Communication Service', 
    help: 'Satellite communication service as alternative to MF/HF',
    reference: 'WBC3 Table 17.2.1'
  },
  { 
    value: 'mf_radio', 
    label: 'MF Radio (Legacy)', 
    help: 'Medium Frequency radio for regional communication',
    reference: 'WBC3 Section 17.3'
  },
  { 
    value: 'ssb_radio', 
    label: 'SSB Radio', 
    help: 'Single Sideband radio for medium-range communication',
    reference: 'WBC3 Section 17.4'
  },
  { 
    value: 'sat_phone', 
    label: 'Satellite Phone', 
    help: 'Satellite communication for global coverage',
    reference: 'GMDSS requirement'
  },
  { 
    value: 'intercom', 
    label: 'Intercom System', 
    help: 'Internal communication system for crew coordination',
    reference: 'WBC3 Section 18.5'
  },
  { 
    value: 'pa_system', 
    label: 'PA/General Alarm', 
    help: 'Public address and general alarm system',
    reference: 'WBC3 Section 15.5'
  }
];

// Electrical Systems options  
export const electricalSystemsOptions = [
  { 
    value: 'plb', 
    label: 'PLB (Personal Locator Beacon)', 
    help: 'Personal emergency locator beacons (one per crew member)',
    reference: 'WBC3 Table 17.2.1'
  },
  { 
    value: 'shore_power', 
    label: 'Shore Power Connection', 
    help: 'Shore power supply system with safety isolation',
    reference: 'WBC3 Section 11.6'
  },
  { 
    value: 'generator', 
    label: 'Generator System', 
    help: 'Emergency or auxiliary generator for electrical power',
    reference: 'WBC3 Section 11.4'
  },
  { 
    value: 'battery_system', 
    label: 'Advanced Battery System', 
    help: 'Large capacity battery systems (beyond basic engine starting)',
    reference: 'WBC3 Annex 1',
    triggersRequirements: ['battery_fire_suppression', 'battery_monitoring']
  },
  { 
    value: 'emergency_power', 
    label: 'Emergency Power System', 
    help: 'Dedicated emergency power supply for essential systems',
    reference: 'WBC3 Section 11.5'
  },
  { 
    value: 'high_voltage', 
    label: 'High Voltage Systems', 
    help: 'Electrical systems above 1000V (special safety requirements)',
    reference: 'WBC3 Section 12.1',
    triggersRequirements: ['high_voltage_training', 'electrical_safety_procedures']
  },
  { 
    value: 'lightning_protection', 
    label: 'Lightning Protection', 
    help: 'Lightning conductor and surge protection systems',
    reference: 'WBC3 Section 12.3'
  }
];

// Operational Enhancement Equipment (No WBC3 regulatory requirement)
export const operationalEquipmentOptions = [
  { 
    value: 'night_vision', 
    label: 'Night Vision Equipment', 
    help: 'Thermal imaging or night vision systems for enhanced navigation',
    reference: 'Optional operational equipment - no WBC3 requirement',
    category: 'operational'
  },
  { 
    value: 'internet', 
    label: 'Internet/WiFi System', 
    help: 'Internet connectivity for weather, charts, and operational communication',
    reference: 'Optional operational equipment - no WBC3 requirement',
    category: 'operational'
  }
];

// Engineering Systems options
export const engineeringSystemsOptions = [
  { 
    value: 'emergency_steering', 
    label: 'Emergency Steering System', 
    help: 'Backup steering system independent of main hydraulics',
    reference: 'WBC3 Section 10.4'
  },
  { 
    value: 'auto_bilge', 
    label: 'Automatic Bilge System', 
    help: 'Automatic bilge pumps with alarm systems',
    reference: 'WBC3 Section 9.3'
  },
  { 
    value: 'engine_monitoring', 
    label: 'Engine Room Monitoring', 
    help: 'Remote monitoring systems for unmanned engine room',
    reference: 'WBC3 Section 8.6'
  },
  { 
    value: 'fire_detection', 
    label: 'Fixed Fire Detection', 
    help: 'Automatic fire detection systems beyond portable equipment',
    reference: 'WBC3 Section 16.2'
  },
  { 
    value: 'co2_suppression', 
    label: 'CO2 Fire Suppression', 
    help: 'Fixed CO2 fire suppression system for engine spaces',
    reference: 'WBC3 Section 16.3',
    mandatoryWhen: 'enginePosition === "below_deck" && engineType === "diesel"'
  },
  { 
    value: 'fuel_monitoring', 
    label: 'Fuel Monitoring System', 
    help: 'Electronic fuel management and monitoring',
    reference: 'WBC3 Section 8.7'
  }
];